<template>
  <div class="call-logs-quick-filter m-t-10">
    <div class="reporting-call-logs-header reporting-settings-v2 p-b-0 m-b-20">
      <div class="reporting-call-logs-header__title">
        Target Dial Insights
      </div>

      <SearchableDropdown
        v-model="tempTimezoneId"
        rules="required"
        :config="timezoneDropdownOptions"
        class="reporting-settings-v2__timezone-selector m-b-10 m-l-auto"
        :clearable="false"
      />

      <DateRangePicker
        date-range-button-class="reporting-settings-v2__date-picker btn"
        class="m-b-10 m-l-10"
        :date-range="timeRange"
        :options="dateRangeConfig"
        :time-zone-id="translatedTimeZoneId"
        @onDateRangeChange="onDateRangeChange"
      />

      <button
        v-if="canExport"
        class="btn m-b-10 m-l-10"
        :disabled="eventInsightsData.allRecords.length < 1 || showLoader"
        @click="exportExcel"
      >
        <span
          class="fa fa-download p-0 m-l-4 visible-xs-inline"
          :class="{'invisible': showLoader}"
        />
        <span class="hidden-xs">{{ $t('EXPORT_CSV') }}</span>
      </button>
    </div>

    <div>
      <div
        v-if="showPartialDataSetMessage"
        class="reporting-grid-notification--partial bg-info m-t-2"
        @click="hidePartialDatasetNotification"
      >
        <i class="fa fa-times pull-right m-t-3 m-l-5" />

        <i class="fa fa-info m-t-3 m-r-5" />
        <span v-t="'PARTIAL_DATA_SET_DATE_RANGE_MESSAGE'" />
      </div>

      <AgGridVue 
        class="ag-grid--custom ag-grid--call-logs ag-grid--call-logs-quick-insights ag-grid--dial-level-insights ag-theme-balham-dark"
        :row-data="eventInsightsData.allRecords"
        :default-col-def="defaultColDef"
        :column-defs="columnDefs"
        :column-types="colTypes"
        :suppress-context-menu="true"
        :suppress-movable-columns="true"
        :overlay-no-rows-template="overlayNoRowsTemplate"
        :suppress-animation-frame="true"
        :suppress-set-column-state-events="true"
        :suppress-property-names-check="true"
        dom-layout="autoHeight"
        @grid-ready="onGridReady"
      />
    </div>
  </div>
</template>

<script>
import { AgGridVue } from '@ag-grid-community/vue';
import moment from 'moment';
import { mapActions, mapGetters, mapState } from 'vuex';

import partialDataSetMixin from '@/modules/reporting/mixins/partialDataSetMixin';
import { accountsService } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import { columnTypes } from '@/modules/reporting/components/AgGridUtils';
import getDateRanges from '@/data/getDateRanges';
import DateRangePicker from '@/core/components/DateRangePicker/DateRangePicker';
import SearchableDropdown from '@/core/components/SearchableDropdown/SearchableDropdown';

let gridApi;

function pinnedRowCellRenderer() {
  return `<div class="ag-grid-pinned-row ag-grid-pinned-row--totals">Totals</div>`;
}

const defaultColDef = {
  flex: 1,
  suppressMenu: true,
};

const aggFieldCommonDef = {
  cellClass: 'ag-right-aligned-cell',
  type: 'long',
  sortable: true,
  resizable: true
};

const columnDefs = [
  {
    field: 'targetName',
    headerName: 'Target',
    pinnedRowCellRenderer,
    sortable: true,
    resizable: true,
    pinned: 'left',
    flex: 2,
    minWidth: 350
  },
  {
    field: 'targetDialed',
    headerName: 'Target Dialed',
    sort: 'desc',
    ...aggFieldCommonDef
  },
  {
    field: 'noAnswer',
    headerName: 'No Answer',
    ...aggFieldCommonDef
  },
  {
    field: 'busyCall',
    headerName: 'Busy Call',
    ...aggFieldCommonDef
  },
  {
    field: 'errorCall',
    headerName: 'Error',
    ...aggFieldCommonDef
  },
  {
    field: 'connectedCall',
    headerName: 'Connected',
    ...aggFieldCommonDef
  },
];

export default {
  name: 'EventInsights',
  components: {
    DateRangePicker,
    AgGridVue,
    SearchableDropdown,
  },

  mixins: [
    partialDataSetMixin
  ],

  data() {
    return {
      columnDefs,
      overlayNoRowsTemplate: 'No data to display. Try selecting a different date range.',
      colTypes: Object.freeze({
        ...columnTypes
      }),
      defaultColDef: Object.freeze(defaultColDef),
      canExport: false
    };
  },

  computed: {
    ...mapState([
      'timezones'
    ]),

    ...mapState('reporting/eventInsights', [
      'dateRange',
      'eventInsightsData',
      'showLoader'
    ]),

    ...mapGetters('reporting/eventInsights', [
      'translatedTimeZoneId',
      'timeRange'
    ]),

    timeZoneId() {
      return this.dateRange?.timeZoneId;
    },

    tempTimezoneId: {
      get() {
        return this.timeZoneId;
      },
      set(value) {
        if (this.timeZoneId !== value) {
          this.setReportingTimezoneId(value);
          this.loadData();
        }
      }
    },

    dateRangeConfig() {
      const timezone = this.translatedTimeZoneId || moment.tz.guess();
      
      return {
        opens: 'left',
        timePicker: true,
        ranges: getDateRanges(timezone)
      };
    },

    timezoneDropdownOptions() {
      return {
        options: this.timezones,
        placeholder: 'Select timezone',
        getOptionLabel: function getOptionLabel(option) {
          return option && option.display;
        },
        reduce: option => option.id,
        clearable: false
      };
    },
  },

  watch: {
    'eventInsightsData.totalsRecord'(newValue) {
      if (newValue && gridApi) {
        gridApi.setPinnedBottomRowData(newValue);
      }
    },
    'eventInsightsData.partialResult'(newValue) {
      if (newValue) {
        this.showPartialDatasetNotification();
      } else {
        this.hidePartialDatasetNotification();
      }
    },
    showLoader: {
      immediate: true,
      handler(newValue) {
        if (!gridApi) {
          return;
        }

        if (newValue) {
          gridApi.showLoadingOverlay();
        } else {
          gridApi.hideOverlay();
        }
      },
    },
  },

  async created() {
    this.getTimezones();
    await this.getReportingTimezoneId();
    this.loadData();
    this.canExport = accountsService.hasAccess('CallLogFileExportJob', 'CREATE');
  },

  beforeDestroy () {
    gridApi = null;
  },

  methods: {
    ...mapActions(['getTimezones']),

    ...mapActions('reporting/eventInsights', [
      'getEventInsights',
      'getReportingTimezoneId',
      'setReportingTimezoneId',
      'setDateRange'
    ]),
    onDateRangeChange(dateRangeDetails) {
      this.setDateRange(dateRangeDetails);
      this.getEventInsights();
    },
    onGridReady(params) {
      gridApi = params.api;
    },
    loadData() {
      this.getEventInsights();
    },
    exportExcel() {
      gridApi.exportDataAsExcel({ sheetName: 'Target Dial Insights' });
    },
  },

};
</script>

<style lang="scss" scoped>
::v-deep .ag-grid--dial-level-insights {
  .ag-overlay-no-rows-wrapper {
    &.ag-layout-auto-height {
      align-items: baseline;
    }
  }
}
</style>